import { cloneDeep } from 'lodash';

const Download = ({
    state = {
        customizations: {},
        customizationsOrder: [],
        types: [],
    },
    content,
} = {}) => {
    return {
        content: content,
        customizations: state.customizations,
        customizationsOrder: state.customizationsOrder,
        availableOptions: state.availableOptions,
        types: state.types,
        typeText: null,
        customizationsText: {},
        worksheet: null,
        previews: [],
        selected: {},
        userHasPremium: state.userHasPremium,
        learnPremiumOptions: !state.userHasPremium,
        requiresPremium: false,
        availableLanguages: state.availableLanguages,
        unsupportedBrowser: false,

        get isDesktop() { return window.innerWidth >= 1280 },
        get pages(){
            const previews = typeof this.previews === 'object' ? this.previews : [];
            return Object.keys(previews).length;
        },
        get hasPreviews(){
            return this.pages > 0;
        },

        init() {
            const defaultType = this.getTypeBy((item) => item.default);
            const defaultOptions = this.getDefaultOptionsFromCustomizations(this.customizations);

            if(!defaultType){
                throw Error(`Expected default type has not passed.`);
            }

            const indexes = this.getSelectedOptions(defaultOptions);
            const selected = [{ type: defaultType.type }, ...indexes];

            const filePath = this.getOptionPath(selected);

            const { text, files } = defaultType;

            this.$watch('worksheet', () => {
                const isPremiumOptionSelected = this.isPremiumOptionSelected(this.selected);

                this.requiresPremium = !this.userHasPremium && isPremiumOptionSelected;
                this.learnPremiumOptions = !this.userHasPremium;

                this.handlePreviewUpdate(files, filePath);
            });

            this.selected = selected;
            this.worksheet = files[filePath];
            this.typeText = text;

            this.handleSelectedUpdated(selected);
        },

        setOption(object = { customization: '', option: '', default: false, premium: false }){
            const { option, customization } = object;

            const selected = this.selected.map((item) => {
                const [key] = Object.keys(item);

                if(key === customization){
                    item[key] = option;
                }

                return item;
            });

            const { type: selectedType } = this.getTypeProperty(selected);

            const type = this.getTypeBy((item) => item.type === selectedType);
            const { text, files } = type;

            const filePath = this.getOptionPath(selected);

            this.selected = selected;
            this.worksheet = files[filePath];
            this.typeText = text;

            this.handleSelectedUpdated(selected);
        },

        handleSelectedUpdated(selected){
            selected.forEach((item) => {
                const [key] = Object.keys(item);

                if(typeof item['type'] === 'string' || typeof this.customizations[key] === 'undefined'){
                    return;
                }

                const { text } = this.customizations[key].find(({ option }) => item[key] === option);

                this.customizationsText[key] = text;
            });
        },

        handlePreviewUpdate(files, filePath) {
            const defaultWorksheet = files[filePath];
            const selected = cloneDeep(this.selected);
            const { file } = this.handleAlternativeWorksheetPreview(selected);

            let preview = defaultWorksheet;

            if(typeof file !== 'undefined') {
                preview = file;
            }

            if(this.unsupportedBrowser){
                preview = null;
            }

            const { previews } = preview;

            this.previews = previews ?? [];
        },

        /** Reset the type to standard, keeping the selected state from others. */
        handleAlternativeWorksheetPreview(selected){
            const { type: standardType, files } = this.getTypeBy((item) => item.default);

            const mutatedSelected = selected.map((item) => {
                const [key] = Object.keys(item);

                if(key === 'type'){
                    item[key] = standardType;
                }

                return item;
            });

            const filePath = this.getOptionPath(mutatedSelected);

            return { file: files[filePath] };
        },

        isOptionSelected(object = { customization: '', option: '' }){
            const { customization, option } = object;

            return this.selected.some((item) => item[customization] && item[customization] === option);
        },

        isDisabledOption(object = { customization: '', option: '' }){
            const { option, customization } = object;

            const selected = cloneDeep(this.selected);

            const index = selected.findIndex(item => {
                const [key] = Object.keys(item);
                return key === customization;
            });

            if (index !== -1) {
                selected[index] = { [customization]: option };
            }

            const { type: selectedType } = this.getTypeProperty(selected);

            const { files } = this.getTypeBy(item => item.type === selectedType);

            const filePath = this.getOptionPath(selected);

            return typeof files[filePath] === 'undefined';
        },

        getSimilarOptionBasedOnSelected(availableOptions, similarOject = { customization: '', option: '' }){
            let index = 0;

            if(availableOptions.length === 1){
                return index;
            }

            const { customization, option } = similarOject;

            const selected = cloneDeep(this.selected);

            const perfectMatches = selected.map(item => {
                const [key] = Object.keys(item);
                return key === customization ? option : item[key];
            }).join('|');

            const splittedPerfectMatches = perfectMatches.split('|');
            const splittedAvailableOptions = availableOptions.map(option => option.split('|'));

            let bestMatch = null;
            let bestMatchScore = 0;

            splittedAvailableOptions.forEach((splittedOption, index) => {
                let score = 0;

                splittedOption.forEach((part, partIndex) => {
                    if (partIndex < splittedPerfectMatches.length && part === splittedPerfectMatches[partIndex]) {
                        score++;
                    }
                });

                if (score > bestMatchScore) {
                    bestMatch = availableOptions[index];
                    bestMatchScore = score;
                }
            });

            const bestMatchIndex = availableOptions.findIndex((path) => path === bestMatch);

            return bestMatchIndex !== -1 && typeof availableOptions[bestMatchIndex] === 'string' ? bestMatchIndex : 0;
        },

        handleAlternativeToDisabledOption(object = { customization: '', option: '' }){
            const availableOptions = this.availableOptions.filter((path) => path.indexOf(object.option) >= 0);

            if(availableOptions.length === 0){
                this.$dispatch('notify', {
                    title: 'Oops!',
                    text: 'That option isn’t available right now. <span class="font-medium">How about trying a different one?</span>',
                    type: 'info'
                });

                throw Error(`Expected available option has not been found for ${object.option}.`);
            }

            const availableOptionIndex = this.getSimilarOptionBasedOnSelected(availableOptions, object);

            const availableOption = availableOptions[availableOptionIndex];

            const options = availableOption.split('|');

            options.forEach((option, index) => {
                if(index === 0){
                    this.setOption({ customization: 'type', option });

                    return
                }

                const customization = Object.keys(this.customizations)
                    .find(
                        (customizationKey, key) => this.customizations[customizationKey]
                            .find(
                                (customization) => customization.option === option)
                        );

                this.setOption({ customization, option });
            });
        },

        getSelectedOptions(options = []){
            return options.map((item) => {
                const key = Object.keys(item);
                return ({ [key]: item[key].option });
            });
        },

        getDefaultOptionsFromCustomizations(customizations){
            return Object.keys(customizations)
                .flatMap(
                    category => ({
                        [category]: customizations[category].find(option => option?.default === true)
                    })
                );
        },

        getOptionPath(customizations = []) {
            const { type } = this.getTypeProperty(customizations);

            let path = `${type}`;

            this.customizationsOrder.forEach((option) => {
                const property = customizations.find(customization => customization.hasOwnProperty(option));

                path += `-${property[option]}`
            });

            return path;
        },

        getTypeProperty(array){
            return array.find(item => item.hasOwnProperty('type'));
        },

        getTypeBy(callback = () => {}){
            return this.types.find(callback);
        },

        isPremiumOptionSelected(selected) {
            return selected.some((item) => {
                const isPremiumFromType = item['type'] && ['fillable', 'customizable', 'example'].includes(item['type']);

                const [key] = Object.keys(item);
                const selectedOption = typeof this.customizations[key] !== 'undefined' &&
                    this.customizations[key].find(({ option }) => item[key] === option);

                return isPremiumFromType || selectedOption?.premium;
            });
        },

        handleWorksheetDownload(){
            const { target } = this.$event;

            const selectedOptions = cloneDeep(this.selected);

            const selected = selectedOptions.map((item) => {
                const [key] = Object.keys(item);

                if(key === 'language') {
                    const language = this.availableLanguages.find(({slug}) => slug === item[key]);

                    return { lang: language?.code ?? 'en' };
                }

                if(key === 'type'){
                    return { download_type: item[key]};
                }

                return { [key.toLowerCase()]: item[key] };
            }).reduce((acc, item) => ({ ...acc, ...item }), {});

            const { type } = this.getTypeProperty(this.selected);

            const { url, slug } = target.dataset;

            const parameters = { content_slug: slug, ...selected };

            if(type === 'standard' || type === 'example') {
                trackEvent('worksheet_download', 'Content', slug, parameters)
                const tab = window.open(url, '_blank');

                if (!tab) {
                    window.location.href = url;
                }
            } else {
                setTimeout(() => this.$dispatch(`show-${type}-instructions-modal`), 200);
                this.$dispatch('update-worksheet-data', { url, data: parameters });
            }
        }
    }
}

export default Download;
